import { type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { Page } from 'lola-framework-ui-test';
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, useGalleryStore } from './galleryStore';
import { galleryScreens } from './screens';
import { galleryFlowScreenClass } from '../constants';

export const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

export const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

/** Wait for fonts + settled images inside a capture root. */
export const settleCaptureRoot = async (root: HTMLElement) => {
  await document.fonts.ready;
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  );
  await waitForPaint();
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

export const captureElementPng = async (
  element: HTMLElement,
  pixelRatio = 2,
): Promise<string> => {
  // Lock the clone to the same laid-out box. Width/height options alone size
  // the canvas; without matching `style`, percentage heights in the clone
  // reflow and the footer gets clipped.
  const width = Math.ceil(element.getBoundingClientRect().width) || CAPTURE_WIDTH;
  const height =
    Math.ceil(element.getBoundingClientRect().height) || CAPTURE_HEIGHT;

  return toPng(element, {
    pixelRatio,
    cacheBust: true,
    width,
    height,
    canvasWidth: Math.round(width * pixelRatio),
    canvasHeight: Math.round(height * pixelRatio),
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: 'none',
      opacity: '1',
      overflow: 'hidden',
    },
  });
};

export const captureElementPngBlob = async (
  element: HTMLElement,
  pixelRatio = 2,
): Promise<Blob> => {
  const dataUrl = await captureElementPng(element, pixelRatio);
  return dataUrlToBlob(dataUrl);
};

type OffscreenHost = {
  host: HTMLDivElement;
  root: Root;
};

const mountOffscreen = (
  Component: ComponentType,
  route: string,
): OffscreenHost => {
  const rawTheme = useGalleryStore.getState().rawTheme;
  const host = document.createElement('div');
  host.className = 'gallery-capture-host';
  host.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${CAPTURE_WIDTH}px`,
    `height:${CAPTURE_HEIGHT}px`,
    'overflow:hidden',
    'pointer-events:none',
    'z-index:-1',
    'background:#fff',
  ].join(';');
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(
    <MemoryRouter initialEntries={[route]}>
      <Page
        font={{
          cdn: rawTheme.font.fontcdn,
          name: rawTheme.font.fontfamily,
        }}
      >
        <div
          className={`gallery-screen gallery-screen--capture ${galleryFlowScreenClass(route)}`.trim()}
          style={{ height: '100%', animation: 'none' }}
        >
          <Component />
        </div>
      </Page>
    </MemoryRouter>,
  );

  return { host, root };
};

const unmountOffscreen = ({ host, root }: OffscreenHost) => {
  root.unmount();
  host.remove();
};

/**
 * Renders a gallery screen offscreen and returns a low-res PNG data URL
 * for the sequence thumbnail list.
 */
export const captureScreenThumb = async (
  screenId: string,
): Promise<string | null> => {
  const screen = galleryScreens.find((entry) => entry.id === screenId);
  if (!screen) return null;

  const mounted = mountOffscreen(screen.Component, screen.route);
  try {
    await wait(120);
    await settleCaptureRoot(mounted.host);
    return await captureElementPng(mounted.host, 0.5);
  } catch {
    return null;
  } finally {
    unmountOffscreen(mounted);
  }
};
